import { formatCurrency } from '../utils/formatters';
import { formatMonthKey } from '../utils/dateUtils';

const buildPrompt = ({ currentMonth, previousMonth, allMonths, accounts }) => {
  const sorted = [...allMonths].sort((a, b) => a.month.localeCompare(b.month));
  const firstMonth = sorted[0];

  const val = (month, name) => parseFloat(month?.values?.[name]) || 0;
  const fmt = formatCurrency;

  const assetLines = accounts.assets.map(a => {
    const curr = val(currentMonth, a.name);
    if (previousMonth) {
      const prev = val(previousMonth, a.name);
      const d = curr - prev;
      return `  • ${a.name}: ${fmt(curr)}  (${d >= 0 ? '+' : ''}${fmt(d)} vs last month)`;
    }
    return `  • ${a.name}: ${fmt(curr)}`;
  }).join('\n');

  const debtLines = accounts.debts.map(a => {
    const curr = val(currentMonth, a.name);
    if (previousMonth) {
      const prev = val(previousMonth, a.name);
      const d = curr - prev;
      return `  • ${a.name}: ${fmt(curr)}  (${d >= 0 ? '+' : ''}${fmt(d)} vs last month)`;
    }
    return `  • ${a.name}: ${fmt(curr)}`;
  }).join('\n');

  const nwDelta = previousMonth ? currentMonth.netWorth - previousMonth.netWorth : null;
  const totalDelta = sorted.length > 1 ? currentMonth.netWorth - firstMonth.netWorth : null;

  return `Financial data for ${formatMonthKey(currentMonth.month)}:

NET WORTH:   ${fmt(currentMonth.netWorth)}${nwDelta != null ? `  (${nwDelta >= 0 ? '+' : ''}${fmt(nwDelta)} vs last month)` : ''}
ASSETS:      ${fmt(currentMonth.totalAssets)}
DEBTS:       ${fmt(currentMonth.totalDebts)}

ASSET BREAKDOWN:
${assetLines || '  (none)'}

DEBT BREAKDOWN:
${debtLines || '  (none)'}

${previousMonth ? `LAST MONTH (${formatMonthKey(previousMonth.month)}):
  Net Worth:  ${fmt(previousMonth.netWorth)}
  Assets:     ${fmt(previousMonth.totalAssets)}
  Debts:      ${fmt(previousMonth.totalDebts)}
` : ''}
HISTORY:
  Months tracked: ${allMonths.length}${totalDelta != null ? `
  Net worth since first entry (${formatMonthKey(firstMonth.month)}): ${totalDelta >= 0 ? '+' : ''}${fmt(totalDelta)}` : ''}

Please provide a detailed monthly financial summary. Structure your response with these sections:

**Financial Health Overview**
A concise assessment of this month's overall position.

**Month-over-Month Changes**
What moved, by how much, and what it signals. Be specific with numbers.

**Account Highlights**
Call out the most notable individual accounts — both wins and concerns.

**Trend Analysis**
What the historical data shows about trajectory and momentum.

**Recommendations**
2–3 specific, actionable steps based on the data.

Be direct and specific. Use the actual dollar figures. Keep each section tight.`;
};

export const generateFinancialSummary = async (apiKey, data) => {
  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are a sharp, knowledgeable personal finance advisor. Analyze monthly wealth tracking data and deliver clear, specific, and actionable insights. Never give generic advice — always tie observations directly to the numbers provided.',
        },
        {
          role: 'user',
          content: buildPrompt(data),
        },
      ],
      max_tokens: 1400,
      temperature: 0.5,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || `OpenAI API error (${response.status})`);
  }

  const json = await response.json();
  return json.choices[0].message.content;
};
